/**
 * クイックスタート記事。
 * 初回送信までの最短手順を 5 ステップで案内する。
 */
import { Callout } from "../components/Callout.js";
import { ScreenshotBox } from "../components/ScreenshotBox.js";

export const meta = {
  title: "クイックスタート",
  updatedAt: "2026-07-23",
};

const stepLabel = {
  display: "inline-block",
  background: "#534AB7",
  color: "white",
  padding: "2px 10px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 600,
  marginRight: 8,
} as const;

export function Content() {
  return (
    <>
      <p>
        Sign Extension をはじめて使うときの最短手順をご案内します。
        初回送信までに必要な準備をひと通り押さえています。
      </p>

      <Callout type="info" title="事前にご準備ください">
        ・HubSpot 管理者権限のアカウント<br />
        ・クラウドサインの法人アカウント（API 利用可能なプラン）<br />
        ・クラウドサインで発行した <strong>Client ID</strong>（取得方法はクラウドサインのサポート窓口へお問い合わせください）
      </Callout>

      {/* ───────────── Step 1 ───────────── */}
      <h2><span style={stepLabel}>STEP 1</span>アカウント登録 + HubSpot 接続</h2>
      <p>
        <a href="https://cs.hsextension.com/register">https://cs.hsextension.com/register</a> から
        当サービスのアカウントを作成します。会社名・氏名・E メール・HubSpot 利用状況を入力し、
        利用規約に同意のうえ「送信」をクリックします。
      </p>
      <Callout type="info" title="登録はメールアドレスのみ">
        新規アカウント登録はメールアドレスでのみ可能です。登録後、Google アカウントを
        使ったログインを有効にすることもできます（ログイン画面の「Google でログイン」）。
      </Callout>
      <ScreenshotBox src="/guide/screenshots/quick-start-1.png" caption="① 登録画面" />
      <p>
        登録の流れの中で HubSpot の承認画面に遷移します。連携したいポータルを選び、
        「<strong>アプリを接続する</strong>」をクリックすると、ダッシュボードに戻ります。
      </p>
      <ScreenshotBox src="/guide/screenshots/quick-start-2.png" caption="② HubSpot 承認画面" />

      <Callout type="warning" title="HubSpot 管理者権限が必要">
        HubSpot 側の承認操作は <strong>HubSpot 管理者権限</strong>を持つアカウントで
        行う必要があります。一般ユーザー権限では承認できません。
      </Callout>

      <Callout type="tip" title="アカウントと HubSpot ポータルの関係">
        当サービスのアカウントは、接続した HubSpot ポータルと 1 対 1 で紐付きます。
        同じ HubSpot ポータルを使う社内のメンバーは、当サービスにユーザー招待して
        同じアカウントを共有する形になります（個別に契約せず、1 つの契約内で
        複数人が使えます）。
      </Callout>

      {/* ───────────── Step 2 ───────────── */}
      <h2><span style={stepLabel}>STEP 2</span>クラウドサイン Client ID を登録</h2>
      <p>
        ダッシュボードの「<strong>接続設定</strong>」タブを開きます。
        「クライアントID一覧」の「<strong>+ 新規追加</strong>」をクリックします。
      </p>
      <ScreenshotBox src="/guide/screenshots/quick-start-3.png" caption="③ クライアントID 一覧と + 新規追加" />
      <p>
        以下を入力して保存します:
      </p>
      <ul>
        <li><strong>ラベル</strong>: 「営業部」「法務担当」など、社内で識別しやすい名前</li>
        <li><strong>Client ID</strong>: クラウドサインの管理画面で発行した文字列</li>
        <li><strong>環境</strong>: 通常は「本番」を選択（検証用は sandbox）</li>
        <li><strong>既定</strong>: チェックを入れた Client ID が送信時のデフォルトになります</li>
      </ul>

      <Callout type="info" title="Client ID の発行手順">
        クラウドサイン管理画面 → チーム設定 → Web API →「利用する」を有効化 →
        送信者ごとに Client ID を発行 → 当サービスに登録、という流れになります。
        詳しくは <a href="/guide/cs-clientid">クラウドサイン Client ID 登録</a> を参照。
      </Callout>

      {/* ───────────── Step 3 ───────────── */}
      <h2><span style={stepLabel}>STEP 3</span>HubSpot プロパティを準備</h2>
      <p>
        「<strong>プロパティ</strong>」タブで、書類を送信したいオブジェクト
        （通常は<strong>取引（Deals）</strong>）の「<strong>設定する</strong>」ボタンを
        クリックします。マッピング設定画面が開きます。
      </p>
      <ScreenshotBox src="/guide/screenshots/quick-start-4.png" caption="④ プロパティマッピング - オブジェクト選択" />
      <p>
        画面上部にある「<strong>取引（Deals）に全プロパティを一括作成</strong>」ボタンを
        押すと、必要なプロパティが HubSpot 側にまとめて作成されます。
        既に存在するプロパティはスキップされます。
      </p>
      <ScreenshotBox src="/guide/screenshots/quick-start-5.png" caption="⑤ 一括作成ボタンとマッピング画面" />

      <Callout type="tip" title="迷ったらまずは一括作成">
        初回は「一括作成」を押して、HubSpot 側にデフォルト名のプロパティを
        作ってしまうのが一番速いです。あとから不要なものは削除可能です。
      </Callout>

      <p>
        画面下部の「<strong>マッピングを保存</strong>」ボタンを押せば設定完了です。
      </p>

      {/* ───────────── Step 4 ───────────── */}
      <h2><span style={stepLabel}>STEP 4</span>HubSpot レコードから書類を送信</h2>
      <p>
        HubSpot で任意の取引レコードを開きます。
        <strong>レコード詳細画面の右側</strong>に「<strong>クラウドサイン</strong>」カードが
        表示されているので、「<strong>クラウドサインで送信</strong>」ボタンをクリックしてください。
      </p>
      <ScreenshotBox src="/guide/screenshots/quick-start-6.png" caption="⑥ 取引レコード上の CRM カード" />

      <Callout type="info" title="CRM カードが表示されない場合">
        HubSpot レコード画面の右上「<strong>カスタマイズ</strong>」から
        「クラウドサイン」カードを追加する必要があります。
        詳しくは <a href="/guide/hubspot-connect">HubSpot との接続</a> を参照してください。
      </Callout>

      <p>
        送信モーダルが開きます。以下の手順で書類を送信できます:
      </p>
      <ol>
        <li><strong>ファイル選択</strong>: PDF ファイルをアップロード（合計 3MB 程度まで）</li>
        <li><strong>宛先指定</strong>: HubSpot から自動取得された宛先を確認し、必要に応じて編集</li>
        <li><strong>確認・送信</strong>: 内容を確認のうえ送信</li>
      </ol>
      <ScreenshotBox src="/guide/screenshots/quick-start-7.png" caption="⑦ 送信モーダル" />

      <p>
        送信が完了すると、HubSpot の対象レコードに以下が自動で書き込まれます:
      </p>
      <ul>
        <li>クラウドサインステータス（「下書き」になります）</li>
        <li>下書き URL（クリックでクラウドサイン側の書類画面へ）</li>
        <li>書類 ID</li>
      </ul>

      <Callout type="tip" title="ステータスは自動で進みます">
        以降は 15 分間隔で、クラウドサインの締結状況を
        HubSpot プロパティへ自動同期します。手動操作は不要です。
        詳しくは <a href="/guide/status-sync">ステータス自動連携</a> をご覧ください。
      </Callout>

      {/* ───────────── 次のステップ ───────────── */}
      <h2>次のステップ</h2>
      <ul>
        <li><a href="/guide/recipients">固定送付宛先・共有先（CC）の設定</a> — 社内承認者を毎回自動で含める</li>
        <li><a href="/guide/clientid-mgmt">複数の Client ID を切り替えて送信</a> — 部署ごとに送信者を変える</li>
        <li><a href="/guide/users-roles">チームメンバーを招待する</a> — 複数ユーザーで共同利用</li>
        <li><a href="/guide/billing">プランをアップグレード</a> — 月間送信数の上限を増やす</li>
      </ul>
    </>
  );
}
